import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import {
  SF009_PDF_SIG_CELL_HEIGHT,
  drawSignatureImageInPdfCell,
  isSigImage,
  signedLabelForList,
  toSigDataUrl,
} from "./sf009FormSignatureUtils";
import CheckTick from "../../assets/icons/Check-Tick.png";
import Sf009SignatureDisplay from "./Sf009SignatureDisplay";
import Sf009TimeSelect from "./Sf009TimeSelect";
import Sf009DiagramImagePreviewModal from "./Sf009DiagramImagePreviewModal";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const CLEARANCE_STATUS_OPTIONS = ["incomplete", "complete"];

const EMPTY_SIG = { signed: "", time: "", date: "" };

function normalizeClearanceStatus(val) {
  let s = String(val ?? "").trim().toLowerCase();
  s = s.replace(/^\*+/, "").trim();
  if (s === "incomplete") return "incomplete";
  if (s === "complete") return "complete";
  if (s === "suspended") return "incomplete";
  return "";
}

function drawPdfTickInCell(doc, data, active) {
  if (!active || data.section !== "body") return;
  const iconSize = 3.4;
  const iconX = data.cell.x + data.cell.width / 2 - iconSize / 2;
  const iconY = data.cell.y + data.cell.height / 2 - iconSize / 2;
  try {
    doc.addImage(CheckTick, "PNG", iconX, iconY, iconSize, iconSize);
  } catch {}
}

function pickSigObject(...candidates) {
  for (const c of candidates) {
    if (c && typeof c === "object" && !Array.isArray(c)) return c;
  }
  return {};
}

const EMPTY_ADDITIONAL_INFO = {
  projectName: "",
  projectNo: "",
  serialNo: "",
  siteLocation: "",
  validUntil: "",
  diagramImages: [],
};

const MAX_DIAGRAM_IMAGE_BYTES = 5 * 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });
}

function normalizeImageSource(value, fallbackMime = "image/jpeg") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  let mime = fallbackMime;
  if (compact.startsWith("/9j")) mime = "image/jpeg";
  else if (compact.startsWith("iVBOR")) mime = "image/png";
  else if (compact.startsWith("R0lGOD")) mime = "image/gif";
  else if (compact.startsWith("UklGR")) mime = "image/webp";
  return `data:${mime};base64,${compact}`;
}

function normalizeDiagramImages(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => normalizeImageSource(v)).filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizeDiagramImages(parsed);
      } catch {}
    }
    const single = normalizeImageSource(trimmed);
    return single ? [single] : [];
  }
  return [];
}

function getPdfImageType(imageSrc, fallbackType = "JPEG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  let b64 = imageSrc;
  const idx = imageSrc.indexOf("base64,");
  if (idx >= 0) b64 = imageSrc.slice(idx + 7);
  b64 = b64.replace(/\s/g, "");
  if (b64.startsWith("/9j")) return "JPEG";
  if (b64.startsWith("iVBOR")) return "PNG";
  if (b64.startsWith("UklGR")) return "WEBP";
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  if (m?.[1]) {
    const subtype = m[1].toLowerCase();
    if (subtype === "png") return "PNG";
    if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
    if (subtype === "webp") return "WEBP";
  }
  return fallbackType;
}

const DEFAULT_FORM_DATA = {
  projectName: "",
  projectNo: "",
  serialNo: "",
  siteLocation: "",
  validUntil: "",
  issue: { to: "", employOf: "", authorisedPersonName: "" },
  equipmentIsolated: "",
  isolationPoints: "",
  provedDeadPoints: "",
  additionalCautionSignsPlacedAt: "",
  specialPrecautions: "",
  issueSignature: { ...EMPTY_SIG },
  receiptSignature: { ...EMPTY_SIG },
  clearanceSignature: { ...EMPTY_SIG },
  clearanceStatus: "",
  cancellationSignature: { ...EMPTY_SIG },
  AdditionalInformation: { ...EMPTY_ADDITIONAL_INFO },
  reasonForSuspension: "",
};

const FIELD_ALIASES = {
  siteLocation: ["site", "site_location", "location"],
  equipmentIsolated: ["equipment", "equipment_isolated"],
  provedDeadPoints: ["provedDead", "proved_dead_points"],
  additionalCautionSignsPlacedAt: ["additionalCautionSignsPlacedAt", "additionalSigns", "signs_barriers"],
  specialPrecautions: ["precautions"],
  issueSignature: ["issue_signature", "issueSignature", "riskAssessmentNumber"],
  reasonForSuspension: ["reason", "reasonForSuspension", "incomplete_reason"],
  clearanceStatus: ["clearance_status", "clearance"],
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function pickAliasedField(src, hd, key, aliases = []) {
  const candidates = [src[key], hd[key]];
  aliases.forEach((a) => {
    candidates.push(src[a], hd[a]);
  });
  for (const v of candidates) {
    if (v != null && v !== "") return v;
  }
  return "";
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  const issueSrc = src.issue && typeof src.issue === "object" ? src.issue : {};
  const issueHd = hd.issue && typeof hd.issue === "object" ? hd.issue : {};
  return {
    projectName: pickAliasedField(src, hd, "projectName"),
    projectNo: pickAliasedField(src, hd, "projectNo"),
    serialNo: pickAliasedField(src, hd, "serialNo"),
    siteLocation: pickAliasedField(src, hd, "siteLocation", FIELD_ALIASES.siteLocation),
    validUntil: pickAliasedField(src, hd, "validUntil"),
    issue: {
      to: issueSrc.to ?? issueHd.to ?? "",
      employOf: issueSrc.employOf ?? issueSrc.employ_of ?? issueHd.employOf ?? issueHd.employ_of ?? "",
      authorisedPersonName:
        issueSrc.authorisedPersonName ??
        issueSrc.authorisedPerson ??
        issueHd.authorisedPersonName ??
        issueHd.authorisedPerson ??
        "",
    },
    equipmentIsolated: pickAliasedField(src, hd, "equipmentIsolated", FIELD_ALIASES.equipmentIsolated),
    isolationPoints: pickAliasedField(src, hd, "isolationPoints"),
    provedDeadPoints: pickAliasedField(src, hd, "provedDeadPoints", FIELD_ALIASES.provedDeadPoints),
    additionalCautionSignsPlacedAt: pickAliasedField(src, hd, "additionalCautionSignsPlacedAt", FIELD_ALIASES.additionalCautionSignsPlacedAt),
    specialPrecautions: pickAliasedField(src, hd, "specialPrecautions", FIELD_ALIASES.specialPrecautions),
    issueSignature: {
      ...EMPTY_SIG,
      ...pickSigObject(src.issueSignature, src.RiskAssessmentNumber, src.riskAssessmentNumber),
    },
    receiptSignature: { ...EMPTY_SIG, ...(src.receiptSignature || {}) },
    clearanceSignature: { ...EMPTY_SIG, ...(src.clearanceSignature || {}) },
    cancellationSignature: { ...EMPTY_SIG, ...(src.cancellationSignature || {}) },
    clearanceStatus: normalizeClearanceStatus(pickAliasedField(src, hd, "clearanceStatus", FIELD_ALIASES.clearanceStatus)),
    AdditionalInformation: (() => {
      const addSrc = src.AdditionalInformation || src.additionalInformation || {};
      return {
        ...EMPTY_ADDITIONAL_INFO,
        ...addSrc,
        diagramImages: normalizeDiagramImages(
          addSrc.diagramImages ?? addSrc.diagram_images ?? addSrc.diagramImage
        ),
      };
    })(),
    reasonForSuspension: pickAliasedField(src, hd, "reasonForSuspension", FIELD_ALIASES.reasonForSuspension),
  };
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009HdrCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
};
const sf009TxtCell = {
  border: sf009TblBorder,
  padding: "10px 12px",
  fontSize: 12,
  background: "#fff",
  lineHeight: 1.55,
  verticalAlign: "top",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 12,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};

function Sf009DElectricalPermitToWorkPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [diagramInputResetKey, setDiagramInputResetKey] = useState(0);
  const [diagramPreview, setDiagramPreview] = useState(null);
  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-d-electrical-permit-to-work", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const displayName = fd.issue?.authorisedPersonName || signedLabelForList(fd.issueSignature) || f.name || "";
    const fields = [fd.siteLocation, fd.projectNo, fd.serialNo, displayName].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.issue?.authorisedPersonName || signedLabelForList(fd.issueSignature) || "";
      next.date = fd.validUntil || fd.issueSignature?.date || "";
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergeRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    const raw = { ...(editForm.formData || {}) };
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-d-electrical-permit-to-work/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          name: editForm.name || raw.issue?.authorisedPersonName || signedLabelForList(raw.issueSignature),
          date: editForm.date || raw.validUntil || raw.issueSignature?.date,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-d-electrical-permit-to-work/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForms(forms.filter((f) => f._id !== deleteId));
        toast.success("Form deleted!");
      } else toast.error(data.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = d instanceof Date ? d : parseSf009DateForPicker(d);
    if (!x || isNaN(x.getTime())) {
      const s = String(d).trim();
      return s || "—";
    }
    return x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const safeDate = parseSf009DateForPicker;

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const raw = mergeRecordIntoDefaults(getRawFormData(record));
    const issue = raw.issue || {};
    const additionalInfo = raw.AdditionalInformation || raw.additionalInformation || {};

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseSf009DateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    let yPosition = await addLogoAndFormHeader(doc, "Electrical Permit to Work");

    const pdfGrid = {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255] };
    const pdfVal = { fillColor: PDF_WHITE, textColor: [0, 0, 0] };
    const pdfHdr = { fillColor: THEME_RGB, fontStyle: "bold", fontSize: 8, textColor: [255, 255, 255] };
    const pdfTxt = { fillColor: PDF_WHITE, valign: "top", overflow: "linebreak", textColor: [0, 0, 0] };
    const pdfSection = { fillColor: THEME_LIGHT_RGB, valign: "top", fontSize: 7.5, overflow: "linebreak", textColor: [0, 0, 0] };

    const ensureSpace = (needed = 22) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = 14;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 22) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        showHead: false,
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable({
      body: [["Electrical Permit to Work"]],
      styles: { ...pdfGrid, ...pdfTxt, fontStyle: "bold", halign: "center", fontSize: 11, minCellHeight: 10 },
    }, 14);

    addPdfTable({
      body: [
        [
          "Project Name:",
          raw.projectName || "",
          "Project No.",
          raw.projectNo || "",
          "Serial No.",
          raw.serialNo || "",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.14, ...pdfLbl },
        1: { cellWidth: contentW * 0.19, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.19, ...pdfVal },
        4: { cellWidth: contentW * 0.12, ...pdfLbl },
        5: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
    }, 16);

    addPdfTable({
      body: [["Site / Location:", raw.siteLocation || "", "Valid Until:", safePdfDate(raw.validUntil)]],
      columnStyles: {
        0: { cellWidth: contentW * 0.14, ...pdfLbl },
        1: { cellWidth: contentW * 0.5, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
    }, 14);

    addPdfTable({
      body: [["Form should be completed legibly by hand (using capital letters) or on screen (as normal text)"]],
      styles: { ...pdfGrid, ...pdfSection, halign: "center" },
    }, 14);

    addPdfTable({
      body: [["1. ISSUE:"]],
      styles: { ...pdfGrid, ...pdfHdr },
    }, 12);

    addPdfTable({
      body: [["To:", issue.to || "", "TTSL Competent Person (E)"]],
      columnStyles: {
        0: { cellWidth: contentW * 0.08, ...pdfLbl },
        1: { cellWidth: contentW * 0.42, ...pdfVal },
        2: { cellWidth: contentW * 0.5, ...pdfTxt, fontStyle: "normal" },
      },
      styles: pdfGrid,
    }, 14);

    addPdfTable({
      body: [["The Employ of", issue.employOf || ""]],
      columnStyles: {
        0: { cellWidth: contentW * 0.18, ...pdfLbl },
        1: { cellWidth: contentW * 0.82, ...pdfVal },
      },
      styles: pdfGrid,
    }, 14);

    addPdfTable({
      body: [["I,", issue.authorisedPersonName || "", "TTSL Authorised Person (E)"]],
      columnStyles: {
        0: { cellWidth: contentW * 0.05, ...pdfLbl },
        1: { cellWidth: contentW * 0.45, ...pdfVal },
        2: { cellWidth: contentW * 0.5, ...pdfTxt, fontStyle: "normal" },
      },
      styles: pdfGrid,
    }, 14);

    addPdfTable({
      body: [
        [
          "Authorised to issue a Permit to Work, hereby declare that it is safe to work on the following Low Voltage equipment which is dead and isolated from all live conductors and has been made secure:-",
        ],
      ],
      styles: { ...pdfGrid, ...pdfSection },
    }, 28);

    addPdfTable({
      body: [[raw.equipmentIsolated || ""]],
      styles: { ...pdfGrid, ...pdfTxt, minCellHeight: 22, fontSize: 8 },
    }, 26);

    addPdfTable({
      body: [["ALL OTHER EQUIPMENT IS DANGEROUS"]],
      styles: { ...pdfGrid, ...pdfTxt, fontStyle: "bold", halign: "center", fontSize: 8 },
    }, 12);

    const labelTextareaRows = [
      [
        "Points at which the system has been isolated, safety locks have been fitted and caution signs and where barriers have been placed:-",
        raw.isolationPoints,
      ],
      ["Points where the system has been proved dead:-", raw.provedDeadPoints],
      ["Additional caution signs have been placed at:-", raw.additionalCautionSignsPlacedAt],
      ["Special precautions that are to be taken by the Competent Person in charge of the work:-", raw.specialPrecautions],
    ];

    const staticLabelOnlyRows = [
      "Other permits or Safety agreements in place associated with the work",
      "The following work may be carried out (give the method statement and Risk Assessment number(s)",
    ];

    labelTextareaRows.forEach(([label, value]) => {
      addPdfTable({
        body: [[label]],
        styles: { ...pdfGrid, ...pdfTxt, fontStyle: "bold", fontSize: 7.5 },
      }, 20);
      addPdfTable({
        body: [[value || ""]],
        styles: { ...pdfGrid, ...pdfTxt, minCellHeight: 18, fontSize: 8 },
      }, 22);
    });

    staticLabelOnlyRows.forEach((label) => {
      addPdfTable({
        body: [[label]],
        styles: { ...pdfGrid, ...pdfTxt, fontStyle: "bold", fontSize: 7.5 },
      }, 16);
    });

    const addPdfSignatureRow = (sigData) => {
      const signedVal = sigData?.signed || "";
      const imgSrc = isSigImage(signedVal) ? toSigDataUrl(signedVal) : "";
      addPdfTable({
        body: [
          [
            "Signed",
            imgSrc ? " " : signedVal,
            "Time",
            sigData?.time || "",
            "Date",
            safePdfDate(sigData?.date),
          ],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.1, ...pdfLbl },
          1: {
            cellWidth: contentW * 0.23,
            ...pdfVal,
            minCellHeight: imgSrc ? SF009_PDF_SIG_CELL_HEIGHT : 10,
            valign: "middle",
          },
          2: { cellWidth: contentW * 0.08, ...pdfLbl },
          3: { cellWidth: contentW * 0.19, ...pdfVal },
          4: { cellWidth: contentW * 0.08, ...pdfLbl },
          5: { cellWidth: contentW * 0.32, ...pdfVal },
        },
        styles: pdfGrid,
        didDrawCell: (data) => drawSignatureImageInPdfCell(doc, data, imgSrc),
      }, imgSrc ? 24 : 14);
    };

    addPdfSignatureRow(raw.issueSignature);

    addPdfTable({
      body: [
        [
          "2. RECEIPT: I acknowledge receipt of this Permit to Work (E) and confirm that locks have been applied at the point of isolation and I will take any additional control measures that may be necessary to carry out the work safely and avoid danger. I hereby declare that I accept responsibility for carrying out the work on the equipment detailed on this Permit to Work and that no attempt will be made by me, or by the persons under my control, to carry out work on any other equipment.",
        ],
      ],
      styles: { ...pdfGrid, ...pdfSection },
    }, 32);

    addPdfSignatureRow(raw.receiptSignature);

    addPdfTable({
      body: [["Being the TTSL Competent Person (E) to whom this Permit to Work is issued."]],
      styles: { ...pdfGrid, ...pdfSection },
    }, 14);

    addPdfTable({
      body: [
        [
          "3. CLEARANCE: I hereby declare that the work for which this Permit to Work was issued is",
          "complete",
          "*suspended",
          "and that all persons under my control have been withdrawn and warned that it is no longer safe to work on the equipment specified on this Permit to Work and that locks, gear and tools have all been cleared.",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.38, ...pdfSection },
        1: { cellWidth: contentW * 0.12, halign: "center", minCellHeight: 12, ...pdfVal },
        2: { cellWidth: contentW * 0.14, halign: "center", minCellHeight: 12, ...pdfVal },
        3: { cellWidth: contentW * 0.36, ...pdfSection },
      },
      styles: { ...pdfGrid, ...pdfSection },
      didDrawCell: (data) => {
        if (data.row.index !== 0) return;
        if (data.column.index === 1) drawPdfTickInCell(doc, data, normalizeClearanceStatus(raw.clearanceStatus) === "complete");
        if (data.column.index === 2) drawPdfTickInCell(doc, data, normalizeClearanceStatus(raw.clearanceStatus) === "incomplete");
      },
    }, 32);

    addPdfSignatureRow(raw.clearanceSignature);

    addPdfTable({
      body: [["Being the TTSL Competent Person (E) to whom this Permit to Work is issued."]],
      styles: { ...pdfGrid, ...pdfSection },
    }, 14);

    addPdfTable({
      body: [
        [
          "4. CANCELLATION: I hereby declare that I have checked that the work has been carried out satisfactorily, that the equipment is safe to be energised and that this Permit to Work is cancelled.",
        ],
      ],
      styles: { ...pdfGrid, ...pdfSection },
    }, 28);

    addPdfSignatureRow(raw.cancellationSignature);

    addPdfTable({
      body: [["Being an TTSL Authorised Person (E) authorised to cancel a Permit to Work (E)."]],
      styles: { ...pdfGrid, ...pdfSection },
    }, 14);

    addPdfTable({
      body: [["5. ADDITIONAL INFORMATION: Attach a diagram and other details if appropriate."]],
      styles: { ...pdfGrid, ...pdfSection },
    }, 14);

    addPdfTable({
      body: [
        [
          "Project Name:",
          additionalInfo.projectName || "",
          "Project No.",
          additionalInfo.projectNo || "",
          "Serial No.",
          additionalInfo.serialNo || "",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.14, ...pdfLbl },
        1: { cellWidth: contentW * 0.19, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.19, ...pdfVal },
        4: { cellWidth: contentW * 0.12, ...pdfLbl },
        5: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
    }, 16);

    addPdfTable({
      body: [
        [
          "Site / Location:",
          additionalInfo.siteLocation || "",
          "Valid Until:",
          safePdfDate(additionalInfo.validUntil),
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.14, ...pdfLbl },
        1: { cellWidth: contentW * 0.5, ...pdfVal },
        2: { cellWidth: contentW * 0.12, ...pdfLbl },
        3: { cellWidth: contentW * 0.24, ...pdfVal },
      },
      styles: pdfGrid,
    }, 14);

    const diagramList = normalizeDiagramImages(
      additionalInfo.diagramImages ?? additionalInfo.diagram_images ?? additionalInfo.diagramImage
    );
    if (diagramList.length) {
      const cols = diagramList.length === 1 ? 1 : 2;
      const gap = 6;
      const cellW = (contentW - (cols - 1) * gap) / cols;
      const cellH = diagramList.length === 1 ? 90 : 58;
      ensureSpace(24);
      doc.setTextColor(...THEME_RGB);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(`Diagram / attachments (${diagramList.length})`, contentX, yPosition + 5);
      yPosition += 10;
      let rowStartY = yPosition;
      for (let i = 0; i < diagramList.length; i += 1) {
        const col = i % cols;
        if (col === 0 && i > 0) rowStartY += cellH + gap;
        if (rowStartY + cellH > pageHeight - PDF_PAGE_BOTTOM) {
          doc.addPage();
          rowStartY = 14;
        }
        const x = contentX + col * (cellW + gap);
        doc.setDrawColor(220, 220, 220);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, rowStartY, cellW, cellH, 2, 2, "FD");
        try {
          const imgType = getPdfImageType(diagramList[i], "JPEG");
          doc.addImage(diagramList[i], imgType, x + 2, rowStartY + 2, cellW - 4, cellH - 4, "", "NONE");
        } catch {}
      }
      yPosition = rowStartY + cellH + 8;
    }

    ensureSpace(60);
    addPdfTable({
      body: [[raw.reasonForSuspension || ""]],
      styles: { ...pdfGrid, ...pdfVal, minCellHeight: 50, valign: "top", fontSize: 8 },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 0) return;
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setPage(pageNumber);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        const labelY = Math.min(data.cell.y + data.cell.height - 5, pageHeight - PDF_PAGE_BOTTOM - 4);
        doc.text("* Reason for Suspension:", data.cell.x + 4, labelY);
      },
    }, 55);

    applyFooterAllPages(doc);

    const displayName = issue.authorisedPersonName || signedLabelForList(raw.issueSignature) || record.name || "Permit";
    const safeSite = String(raw.siteLocation || "Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || raw.validUntil || raw.issueSignature?.date) || "form").replace(
      /[\\/:"*?<>|]+/g,
      "-"
    );
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("D", getSf009SharePointFields("D", raw, record, getUserName))
        : `SF009D_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.siteLocation || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-D Electrical Permit to Work PDF generated successfully!");
    }
  };

  const renderStaticLabelRow = (label) => (
    <tr>
      <td colSpan={6} style={{ ...sf009TxtCell, fontWeight: 600, lineHeight: 1.45 }}>
        {label}
      </td>
    </tr>
  );

  const renderLabelTextareaRow = (label, fieldKey, rows = 3) => (
    <tr>
      <td colSpan={6} style={{ ...sf009TxtCell, padding: 0 }}>
        <div style={{ padding: "8px 12px", fontWeight: 600, lineHeight: 1.45 }}>{label}</div>
        <textarea
          rows={rows}
          value={fd[fieldKey] || ""}
          onChange={(e) => updateFormData("_flat", fieldKey, e.target.value)}
          style={{ ...sf009CellInput, width: "100%", minHeight: rows * 22, resize: "vertical", padding: "8px 12px 12px" }}
        />
      </td>
    </tr>
  );

  const addInfo = fd.AdditionalInformation || EMPTY_ADDITIONAL_INFO;
  const diagramImages = normalizeDiagramImages(addInfo.diagramImages);

  const handleDiagramSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (!imageFiles.length) {
      toast.error("Please select image files.");
      setDiagramInputResetKey((k) => k + 1);
      return;
    }
    const oversized = imageFiles.find((f) => f.size > MAX_DIAGRAM_IMAGE_BYTES);
    if (oversized) {
      toast.error("Each image must be 5 MB or smaller.");
      setDiagramInputResetKey((k) => k + 1);
      return;
    }
    try {
      const dataUrls = await Promise.all(imageFiles.map((file) => fileToDataUrl(file)));
      const valid = dataUrls.filter(Boolean);
      if (!valid.length) {
        toast.error("Failed to load images.");
        return;
      }
      updateFormData("AdditionalInformation", "diagramImages", [...diagramImages, ...valid]);
    } catch {
      toast.error("Failed to load images.");
    } finally {
      setDiagramInputResetKey((k) => k + 1);
    }
  };

  const removeDiagramAtIndex = (index) => {
    updateFormData(
      "AdditionalInformation",
      "diagramImages",
      diagramImages.filter((_, idx) => idx !== index)
    );
  };

  const renderDiagramUploadRow = () => (
    <tr>
      <td colSpan={6} style={{ ...sf009ValCell, padding: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 12, color: THEME }}>Attach diagram / images</span>
          <span
            style={{
              background: THEME_LIGHT,
              color: "#334155",
              border: `1px solid ${THEME_BORDER}`,
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {diagramImages.length} image{diagramImages.length === 1 ? "" : "s"}
          </span>
        </div>
        <input
          key={diagramInputResetKey}
          type="file"
          accept="image/*"
          multiple
          onChange={handleDiagramSelect}
          className="form-control form-control-sm"
          style={{ borderColor: THEME_BORDER, borderRadius: 8, marginBottom: 10, fontSize: 12 }}
        />
        <div
          style={{
            border: `1px dashed ${THEME_BORDER}`,
            borderRadius: 8,
            minHeight: 100,
            background: "#fafbfc",
            padding: 10,
          }}
        >
          {diagramImages.length ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
              {diagramImages.map((img, idx) => (
                <div
                  key={`${idx}-${String(img).slice(0, 24)}`}
                  style={{
                    position: "relative",
                    border: `1px solid ${THEME_BORDER}`,
                    borderRadius: 8,
                    background: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={normalizeImageSource(img)}
                    alt={`Diagram ${idx + 1}`}
                    title="Click to preview"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setDiagramPreview({
                        src: normalizeImageSource(img),
                        title: `Diagram ${idx + 1}`,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDiagramPreview({
                          src: normalizeImageSource(img),
                          title: `Diagram ${idx + 1}`,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      height: 110,
                      objectFit: "contain",
                      display: "block",
                      background: "#f8fafc",
                      cursor: "pointer",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "5px 8px",
                      borderTop: `1px solid ${THEME_BORDER}`,
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>Image {idx + 1}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDiagramAtIndex(idx);
                      }}
                      style={{
                        padding: "2px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        border: "1px solid #dc2626",
                        borderRadius: 6,
                        background: "#fff",
                        color: "#dc2626",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                minHeight: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                gap: 4,
                fontSize: 12,
              }}
            >
              <span>No diagram uploaded yet</span>
            </div>
          )}
        </div>
        {diagramImages.length > 0 && (
          <button
            type="button"
            onClick={() => updateFormData("AdditionalInformation", "diagramImages", [])}
            style={{
              marginTop: 8,
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid #dc2626",
              borderRadius: 6,
              background: "#fff",
              color: "#dc2626",
              cursor: "pointer",
            }}
          >
            Remove all images
          </button>
        )}
      </td>
    </tr>
  );

  const renderAdditionalInformationHeader = () => (
    <>
      <tr>
        <td style={{ ...sf009LblCell, width: "14%" }}>Project Name:</td>
        <td style={{ ...sf009ValCell, width: "19%" }}>
          <input
            type="text"
            value={addInfo.projectName || ""}
            onChange={(e) => updateFormData("AdditionalInformation", "projectName", e.target.value)}
            style={sf009CellInput}
          />
        </td>
        <td style={{ ...sf009LblCell, width: "12%" }}>Project No.</td>
        <td style={{ ...sf009ValCell, width: "19%" }}>
          <input
            type="text"
            value={addInfo.projectNo || ""}
            onChange={(e) => updateFormData("AdditionalInformation", "projectNo", e.target.value)}
            style={sf009CellInput}
          />
        </td>
        <td style={{ ...sf009LblCell, width: "12%" }}>Serial No.</td>
        <td style={{ ...sf009ValCell, width: "24%" }}>
          <input
            type="text"
            value={addInfo.serialNo || ""}
            onChange={(e) => updateFormData("AdditionalInformation", "serialNo", e.target.value)}
            style={sf009CellInput}
          />
        </td>
      </tr>
      <tr>
        <td style={sf009LblCell}>Site / Location:</td>
        <td colSpan={3} style={sf009ValCell}>
          <input
            type="text"
            value={addInfo.siteLocation || ""}
            onChange={(e) => updateFormData("AdditionalInformation", "siteLocation", e.target.value)}
            style={sf009CellInput}
          />
        </td>
        <td style={sf009LblCell}>Valid Until:</td>
        <td style={sf009ValCell}>
          <DatePicker
            selected={safeDate(addInfo.validUntil)}
            onChange={(d) => updateFormData("AdditionalInformation", "validUntil", d ? datePickerToSf009DateString(d) : "")}
            dateFormat="dd-MM-yyyy"
            className="form-control form-control-sm border-0 p-0"
            placeholderText="dd-MM-yyyy"
            wrapperClassName="w-100"
          />
        </td>
      </tr>
    </>
  );

  const renderSignatureFields = (sectionKey) => {
    const sig = fd[sectionKey] || EMPTY_SIG;
    return (
      <tr>
        <td style={{ ...sf009LblCell, width: "10%", verticalAlign: "top" }}>Signed</td>
        <td style={{ ...sf009ValCell, width: "23%", padding: "6px 8px", verticalAlign: "top" }}>
          <div
            style={{
              border: `1px solid ${THEME_BORDER}`,
              borderRadius: 8,
              background: "#fafbfc",
              overflow: "hidden",
              minHeight: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px 6px",
            }}
          >
            <Sf009SignatureDisplay value={sig} />
          </div>
        </td>
        <td style={{ ...sf009LblCell, width: "8%", verticalAlign: "middle" }}>Time</td>
        <td style={{ ...sf009ValCell, width: "19%", verticalAlign: "middle" }}>
          <Sf009TimeSelect
            value={sig.time || ""}
            onChange={(v) => updateFormData(sectionKey, "time", v)}
          />
        </td>
        <td style={{ ...sf009LblCell, width: "8%", verticalAlign: "middle" }}>Date</td>
        <td style={{ ...sf009ValCell, width: "32%", verticalAlign: "middle" }}>
          <DatePicker
            selected={safeDate(sig.date)}
            onChange={(d) => updateFormData(sectionKey, "date", d ? datePickerToSf009DateString(d) : "")}
            dateFormat="dd-MM-yyyy"
            className="form-control form-control-sm border-0 p-0"
            placeholderText="dd-MM-yyyy"
            wrapperClassName="w-100"
          />
        </td>
      </tr>
    );
  };

  const renderPermitForm = () => (
    <div style={{ border: sf009TblBorder, background: "#fff" }}>
      <div
        style={{
          textAlign: "center",
          padding: "14px 12px",
          fontWeight: 700,
          fontSize: 15,
          color: "#1a202c",
          borderBottom: sf009TblBorder,
          background: "#fff",
        }}
      >
        Electrical Permit to Work
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>
          <tr>
            <td style={{ ...sf009LblCell, width: "14%" }}>Project Name:</td>
            <td style={{ ...sf009ValCell, width: "19%" }}>
              <input
                type="text"
                value={fd.projectName || ""}
                onChange={(e) => updateFormData("_flat", "projectName", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={{ ...sf009LblCell, width: "12%" }}>Project No.</td>
            <td style={{ ...sf009ValCell, width: "19%" }}>
              <input
                type="text"
                value={fd.projectNo || ""}
                onChange={(e) => updateFormData("_flat", "projectNo", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={{ ...sf009LblCell, width: "12%" }}>Serial No.</td>
            <td style={{ ...sf009ValCell, width: "24%" }}>
              <input
                type="text"
                value={fd.serialNo || ""}
                onChange={(e) => updateFormData("_flat", "serialNo", e.target.value)}
                style={sf009CellInput}
              />
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>Site / Location:</td>
            <td colSpan={3} style={sf009ValCell}>
              <input
                type="text"
                value={fd.siteLocation || ""}
                onChange={(e) => updateFormData("_flat", "siteLocation", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td style={sf009LblCell}>Valid Until:</td>
            <td style={sf009ValCell}>
              <DatePicker
                selected={safeDate(fd.validUntil)}
                onChange={(d) => updateFormData("_flat", "validUntil", d ? datePickerToSf009DateString(d) : "")}
                dateFormat="dd-MM-yyyy"
                className="form-control form-control-sm border-0 p-0"
                placeholderText="dd-MM-yyyy"
                wrapperClassName="w-100"
              />
            </td>
          </tr>
          <tr>
            <td
              colSpan={6}
              style={{
                ...sf009TxtCell,
                background: THEME_LIGHT,
                textAlign: "center",
                fontSize: 11,
              }}
            >
              Form should be completed legibly by hand (using capital letters) or on screen (as normal text)
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={sf009HdrCell}>
              1. ISSUE:
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>To:</td>
            <td colSpan={2} style={sf009ValCell}>
              <input
                type="text"
                value={fd.issue?.to || ""}
                onChange={(e) => updateFormData("issue", "to", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td colSpan={3} style={sf009TxtCell}>
              TTSL Competent Person (E)
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>The Employ of</td>
            <td colSpan={5} style={sf009ValCell}>
              <input
                type="text"
                value={fd.issue?.employOf || ""}
                onChange={(e) => updateFormData("issue", "employOf", e.target.value)}
                style={sf009CellInput}
              />
            </td>
          </tr>
          <tr>
            <td style={sf009LblCell}>I,</td>
            <td colSpan={2} style={sf009ValCell}>
              <input
                type="text"
                value={fd.issue?.authorisedPersonName || ""}
                onChange={(e) => updateFormData("issue", "authorisedPersonName", e.target.value)}
                style={sf009CellInput}
              />
            </td>
            <td colSpan={3} style={sf009TxtCell}>
              TTSL Authorised Person (E)
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              Authorised to issue a Permit to Work, hereby declare that it is safe to work on the following Low Voltage equipment which is dead and isolated from all live conductors and has been made secure:-
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009ValCell, padding: 0 }}>
              <textarea
                rows={4}
                value={fd.equipmentIsolated || ""}
                onChange={(e) => updateFormData("_flat", "equipmentIsolated", e.target.value)}
                style={{ ...sf009CellInput, width: "100%", minHeight: 88, resize: "vertical", padding: "10px 12px" }}
              />
            </td>
          </tr>
          <tr>
            <td
              colSpan={6}
              style={{ ...sf009TxtCell, fontWeight: 700, textAlign: "center", fontSize: 11 }}
            >
              ALL OTHER EQUIPMENT IS DANGEROUS
            </td>
          </tr>
          {renderLabelTextareaRow(
            "Points at which the system has been isolated, safety locks have been fitted and caution signs and where barriers have been placed:-",
            "isolationPoints",
            3
          )}
          {renderLabelTextareaRow("Points where the system has been proved dead:-", "provedDeadPoints", 3)}
          {renderLabelTextareaRow("Additional caution signs have been placed at:-", "additionalCautionSignsPlacedAt", 3)}
          {renderLabelTextareaRow(
            "Special precautions that are to be taken by the Competent Person in charge of the work:-",
            "specialPrecautions",
            3
          )}
          {renderStaticLabelRow("Other permits or Safety agreements in place associated with the work")}
          {renderStaticLabelRow(
            "The following work may be carried out (give the method statement and Risk Assessment number(s)"
          )}
          {renderSignatureFields("issueSignature")}
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              2. RECEIPT: I acknowledge receipt of this Permit to Work (E) and confirm that locks have been applied at the point of isolation and I will take any additional control measures that may be necessary to carry out the work safely and avoid danger. I hereby declare that I accept responsibility for carrying out the work on the equipment detailed on this Permit to Work and that no attempt will be made by me, or by the persons under my control, to carry out work on any other equipment.
            </td>
          </tr>
          {renderSignatureFields("receiptSignature")}
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              Being the TTSL Competent Person (E) to whom this Permit to Work is issued.
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11, lineHeight: 1.6 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                <span>
                  3. CLEARANCE: I hereby declare that the work for which this Permit to Work was issued is
                </span>
                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="clearanceStatus"
                    className="form-check-input theme-checkbox"
                    checked={normalizeClearanceStatus(fd.clearanceStatus) === "complete"}
                    onChange={() => updateFormData("_flat", "clearanceStatus", "complete")}
                    style={{ width: 16, height: 16, margin: 0, accentColor: THEME, cursor: "pointer" }}
                  />
                  <span>complete</span>
                </label>
                <span>/</span>
                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="clearanceStatus"
                    className="form-check-input theme-checkbox"
                    checked={normalizeClearanceStatus(fd.clearanceStatus) === "incomplete"}
                    onChange={() => updateFormData("_flat", "clearanceStatus", "incomplete")}
                    style={{ width: 16, height: 16, margin: 0, accentColor: THEME, cursor: "pointer" }}
                  />
                  <span>*suspended</span>
                </label>
                <span>
                  and that all persons under my control have been withdrawn and warned that it is no longer safe to work on the equipment specified on this Permit to Work and that locks, gear and tools have all been cleared.
                </span>
              </div>
            </td>
          </tr>
          {renderSignatureFields("clearanceSignature")}
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              Being the TTSL Competent Person (E) to whom this Permit to Work is issued.
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              4. CANCELLATION: I hereby declare that I have checked that the work has been carried out satisfactorily, that the equipment is safe to be energised and that this Permit to Work is cancelled.
            </td>
          </tr>
          {renderSignatureFields("cancellationSignature")}
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              Being an TTSL Authorised Person (E) authorised to cancel a Permit to Work (E).
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ ...sf009TxtCell, background: THEME_LIGHT, fontSize: 11 }}>
              5. ADDITIONAL INFORMATION: Attach a diagram and other details if appropriate.
            </td>
          </tr>
          {renderAdditionalInformationHeader()}
          {renderDiagramUploadRow()}
          <tr>
            <td colSpan={6} style={{ ...sf009ValCell, padding: 0, position: "relative" }}>
              <textarea
                rows={5}
                value={fd.reasonForSuspension || ""}
                onChange={(e) => updateFormData("_flat", "reasonForSuspension", e.target.value)}
                style={{
                  ...sf009CellInput,
                  width: "100%",
                  minHeight: 110,
                  resize: "vertical",
                  padding: "10px 12px 28px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  bottom: 8,
                  fontWeight: 600,
                  fontSize: 12,
                  pointerEvents: "none",
                }}
              >
                * Reason for Suspension:
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>
            SF-009-D Electrical Permit to Work
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site/Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Serial No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Valid Until</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const displayName = raw.issue?.authorisedPersonName || signedLabelForList(raw.issueSignature) || r.name || "—";
                  const updatedDate = r.updatedAt || r.createdAt || r.date || raw.validUntil;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.siteLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.serialNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{displayName}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.date || raw.validUntil)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.siteLocation || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      <Sf009DiagramImagePreviewModal
        src={diagramPreview?.src}
        title={diagramPreview?.title}
        onClose={() => setDiagramPreview(null)}
      />

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(26, 32, 44, 0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 920, margin: "0 auto", border: "none", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(139, 22, 52, 0.15)" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>
                  Edit SF-009-D Electrical Permit to Work
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "20px 24px 8px", background: "#fff" }}>
                {renderPermitForm()}
              </div>
              <div className="modal-footer" style={{ background: "#fff", borderTop: `1px solid ${THEME_BORDER}`, padding: "14px 24px", gap: 10 }}>
                <button type="button" className="btn btn-light" style={{ borderRadius: 8, fontWeight: 600, border: `1px solid ${THEME_BORDER}` }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 22px", border: "none" }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-D Electrical Permit to Work File"
        importPath="sf-009-d-electrical-permit-to-work/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-D Electrical Permit to Work</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009DElectricalPermitToWorkPage;
